import { useState } from "react";
import { Target, Lightning, Fire, TrendUp, X, Check } from "@phosphor-icons/react";
import { useAuth } from "../../hooks/useAuth";
import { useGamification } from "../../hooks/useGamification";
import { ScoreMap } from "./CurrentWeek";
import { motion } from "framer-motion";
import LiveClassBanner from "./LiveClassBanner";
import LevelDetailModal from "../../components/LevelDetailModal";
import StreakCalendarModal from "../../components/StreakCalendarModal";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return "İyi geceler";
  if (hour < 12) return "Günaydın";
  if (hour < 18) return "İyi günler";
  return "İyi akşamlar";
}

type ModalType = "tasks" | "success" | "streak" | "level" | null;

const COPY = {
  week: {
    subtitle: "Bu haftaki ilerlemeni takip et, görevlerini tamamla.",
    tasksLabel: "Bu hafta",
    avgLabel: "Haftalık ort.",
    tasksIntro: "Bu hafta atanan görevlerden",
    avgIntro: "Bu haftaki tüm görevlerindeki skorların ortalaması",
  },
  module: {
    subtitle: "Kaldığın modüldeki ilerlemeni takip et, görevlerini tamamla.",
    tasksLabel: "Bu modül",
    avgLabel: "Modül ort.",
    tasksIntro: "Bu modüldeki görevlerden",
    avgIntro: "Bu modüldeki tüm görevlerindeki skorların ortalaması",
  },
} as const;

export default function DashboardHero({
  scoreMap,
  bulkItems,
  context = "week",
}: {
  scoreMap?: ScoreMap;
  bulkItems: { content_id: number; type: string; name?: string; tag?: string }[];
  context?: "week" | "module";
}) {
  const { user } = useAuth();
  const { stats } = useGamification();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const copy = COPY[context];

  let completedTasks = 0;
  let totalCompletion = 0;
  const taskDetails: { name: string; score: number; tag: string }[] = [];

  if (scoreMap) {
    for (const bi of bulkItems) {
      const key = `${bi.content_id}_${bi.type}`;
      const score = scoreMap[key]?.score ?? 0;
      totalCompletion += score;
      if (score >= 79) completedTasks++;
      taskDetails.push({
        name: bi.name || `#${bi.content_id}`,
        score,
        tag: bi.tag || bi.type,
      });
    }
  }
  const totalTasks = bulkItems.length;
  const avgCompletion = totalTasks > 0 ? Math.round(totalCompletion / totalTasks) : 0;

  const greeting = getGreeting();
  const firstName = user?.shortName?.split(" ")[0] || "Öğrenci";

  const statCards = [
    {
      key: "tasks" as ModalType,
      icon: Target,
      iconBg: "bg-myGreen/10",
      iconColor: "text-myGreen",
      value: `${completedTasks}/${totalTasks}`,
      label: copy.tasksLabel,
    },
    {
      key: "success" as ModalType,
      icon: TrendUp,
      iconBg: "bg-myBlue/10",
      iconColor: "text-myBlue",
      value: `%${avgCompletion}`,
      label: copy.avgLabel,
    },
    {
      key: "streak" as ModalType,
      icon: Fire,
      iconBg: "bg-myAmber/10",
      iconColor: "text-myAmber",
      value: `${stats.currentStreak}`,
      label: "Gün seri",
      weight: "fill" as const,
    },
    {
      key: "level" as ModalType,
      icon: Lightning,
      iconBg: "bg-myPurple/10",
      iconColor: "text-myPurple",
      value: `Sv.${stats.level}`,
      label: `${stats.totalXp} XP`,
      weight: "fill" as const,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Greeting */}
      <div className="pl-12 lg:pl-0">
        <h1 className="text-xl sm:text-2xl font-semibold">
          {greeting}, <span className="text-myGreen">{firstName}</span>
        </h1>
        <p className="text-white/40 text-sm mt-1">
          {copy.subtitle}
        </p>
      </div>

      {/* Stats — 4 inline */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {statCards.map((card) => (
          <button
            key={card.label}
            onClick={() => setActiveModal(card.key)}
            className="bg-surface-1 border border-white/[0.06] rounded-xl px-4 py-4 flex items-center justify-center gap-3 hover:border-white/15 transition-colors cursor-pointer"
          >
            <div className={`p-2 ${card.iconBg} rounded-lg shrink-0`}>
              <card.icon
                size={18}
                className={card.iconColor}
                weight={card.weight || "bold"}
              />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold leading-tight">{card.value}</p>
              <p className="text-xs text-white/35 truncate">{card.label}</p>
            </div>
          </button>
        ))}
      </div>


      {/* Live Class */}
      <LiveClassBanner compact />

      {/* Modals */}
      <StreakCalendarModal
        isOpen={activeModal === "streak"}
        onClose={() => setActiveModal(null)}
        stats={stats}
      />
      <LevelDetailModal
        isOpen={activeModal === "level"}
        onClose={() => setActiveModal(null)}
        stats={stats}
      />
      <InfoModal
        isOpen={activeModal === "tasks"}
        onClose={() => setActiveModal(null)}
        title="Tamamlanan Görevler"
        icon={<Target size={24} className="text-myGreen" weight="bold" />}
        iconBg="bg-myGreen/10"
      >
        <p className="text-white/50 text-sm leading-relaxed mb-4">
          {copy.tasksIntro} <span className="text-white font-semibold">{completedTasks}</span> tanesini
          tamamladın. Bir görev <span className="text-myGreen font-semibold">%79 ve üzeri</span> skor aldığında
          tamamlanmış sayılır.
        </p>
        <div className="space-y-1">
          {taskDetails.map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-2 border-b border-white/[0.04] last:border-0">
              <span className={`text-[9px] font-bold uppercase tracking-wider shrink-0 w-10 ${t.tag === "PROJECT" ? "text-myOrange" : "text-myBlue"}`}>
                {t.tag === "PROJECT" ? "Proje" : "Ders"}
              </span>
              <span className="text-white/60 flex-1 truncate">{t.name}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={t.score >= 79 ? "text-myGreen font-semibold" : "text-white/35"}>
                  %{t.score}
                </span>
                {t.score >= 79 && <Check size={14} className="text-myGreen" weight="bold" />}
              </div>
            </div>
          ))}
        </div>
      </InfoModal>
      <InfoModal
        isOpen={activeModal === "success"}
        onClose={() => setActiveModal(null)}
        title="Ortalama Başarı"
        icon={<TrendUp size={24} className="text-myBlue" weight="bold" />}
        iconBg="bg-myBlue/10"
      >
        <p className="text-white/50 text-sm leading-relaxed mb-3">
          {copy.avgIntro} <span className="text-white font-semibold">%{avgCompletion}</span>.
        </p>
        <div className="bg-white/[0.03] rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-white/35">Toplam görev</span>
            <span className="font-semibold">{totalTasks}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-white/35">Tamamlanan</span>
            <span className="font-semibold text-myGreen">{completedTasks}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-white/35">Devam eden</span>
            <span className="font-semibold text-myOrange">{totalTasks - completedTasks}</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-2">
            <div
              className="h-full rounded-full bg-gradient-to-r from-myBlue to-myGreen"
              style={{ width: `${avgCompletion}%` }}
            />
          </div>
        </div>
        <p className="text-white/30 text-xs mt-3">
          Her görevde %79 ve üzeri skor almayı hedefle!
        </p>
      </InfoModal>
    </div>
  );
}

function InfoModal({
  isOpen,
  onClose,
  title,
  icon,
  iconBg,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        className="bg-surface-1 border border-white/[0.08] rounded-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 ${iconBg} rounded-xl`}>{icon}</div>
            <h2 className="text-base font-semibold">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </motion.div>
    </div>
  );
}
